import React from "react";
import { connect } from "react-redux";

import Dialog from "../core/components/dialog/Dialog";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogOK from "../core/components/dialog/DialogOK";
import File from "../core/types/files/File";
import FileExplorer from "../core/components/fileExplorer/FileExplorer";
import Grid from "../core/components/grid/Grid";
import GridColumn, { GridColumnType } from "../core/components/grid/GridColumn";
import Import from "../core/components/import/Import";
import Info from "../core/components/info/Info";
import InputForm from "../core/components/inputform/InputForm";
import InputFormField from "../core/components/inputform/InputFormField";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import Panel from "../core/components/panels/Panel";
import SelectList from "../core/components/inputform/SelectList";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import Wizard from "../core/components/wizard/Wizard";
import WizardStep from "../core/components/wizard/WizardStep";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { AppDispatch, IAppState } from "../core/state/Store";
import { DataSet } from "../core/types/data/DataSet";
import { DataTable } from "../core/types/data/DataTable";
import { FileExplorerTypes } from "../core/types/system/FileExplorer";
import {
    ImportLog,
    SkippedRecordCount,
    UpsertCount,
} from "../core/types/core/ImportLog";
import Area from "../core/types/core/Area";
import {
    addArea,
    archiveArea,
    importAreasFromTD,
    initialiseAreaDetails,
    updateArea,
} from "../core/state/core/Areas";
import { initialiseCampusDetails } from "../core/state/core/Campus";
import Campus from "../core/types/core/Campus";
import { Alignment, Side } from "../core/types/system/Layout";
import {
    SpreadsheetConfiguration,
    SpreadsheetTableDefinition,
} from "../core/types/data/SpreadsheetConfiguration";
import YearLevel from "../core/types/core/YearLevel";
import Toggle from "../core/components/inputform/Toggle";
import Text from "../core/components/text/Text";
import { addAlert } from "../core/state/system/Alerts";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import RenderOrientations from "../core/types/enums/RenderOrientation";
import InputFormMandatory from "../core/components/inputform/InputFormMandatory";
import SortDialog from "../core/components/sorting/SortDialog";
import Utility from "../core/foundation/Utility";
import InputFieldTypes from "../core/types/enums/ImportFieldTypes";

import template from "../resources/Area_Import_Template.xlsx";

import "./Areas.scss";

interface RouteAreasProps {
    campuses: Campus[];
    archives: Area[];
    areas: Area[];
    yearLevels: YearLevel[];

    setPageLoading: (loading: boolean) => void;
    initialiseAreaDetails: () => Promise<void>;
    initialiseCampusDetails: () => Promise<void>;
    addArea: (details: Area[]) => Promise<void>;
    updateArea: (details: Area[]) => Promise<void>;
    archiveArea: (details: Area[]) => Promise<void>;
}

interface RouteAreasState {
    archives: boolean;

    editOpen: boolean;
    editLoad: boolean;
    editTitle: string;
    editStep: number;

    sorting: Area[];
    sortOpen: boolean;
    sortLoad: boolean;

    importOpen: boolean;
    importLoad: boolean;
    importType: string;
    importStep: number;
    importWidth: number;
    importValid: boolean;

    selection: Area | null;
    selections: Area[];

    editing: Area | null;
}

class RouteAreas extends React.Component<RouteAreasProps, RouteAreasState> {
    state: RouteAreasState = {
        archives: false,

        editOpen: false,
        editLoad: false,
        editTitle: "",
        editStep: 0,

        sorting: [],
        sortOpen: false,
        sortLoad: false,

        importOpen: false,
        importLoad: false,
        importType: undefined,
        importStep: 0,
        importWidth: 400,
        importValid: false,

        selection: null,
        selections: [],

        editing: null,
    };

    static getDerivedStateFromProps(
        props: RouteAreasProps,
        state: RouteAreasState,
    ) {
        return {
            importWidth:
                state.importStep === 0
                    ? state.importType === "csv"
                        ? 1000
                        : state.importType === "tdf"
                        ? 580
                        : 580
                    : 580,
        };
    }

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);

            await Promise.all([
                this.props.initialiseAreaDetails(),
                this.props.initialiseCampusDetails(),
            ]);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    add() {
        this.setState({
            editing: new Area({
                id: 0,
                code: "",
            }),
            editOpen: true,
            editLoad: false,
            editTitle: "Add Duty Area",
            editStep: 0,
        });
    }

    async edit() {
        if (!this.state.selection) return;

        try {
            this.props.setPageLoading(true);

            const temp = new Area(this.state.selection);
            this.setState({
                editing: temp,
                editOpen: true,
                editLoad: false,
                editTitle: "Edit Duty Area",
                editStep: 0,
            });
        } finally {
            this.props.setPageLoading(false);
        }
    }

    cancelEdit() {
        this.setState({ editing: null, editOpen: false });
    }

    canSave(): boolean {
        if (!this.state.editing) return false;

        return !!(this.state.editing.code && this.state.editing.name);
    }

    async saveEdit() {
        if (!this.state.editing) return;

        try {
            this.setState({ editLoad: true });

            if (this.state.editing.id === 0)
                await this.props.addArea([this.state.editing]);
            else await this.props.updateArea([this.state.editing]);
        } finally {
            this.setState({ editing: null, editLoad: false, editOpen: false });
        }
    }

    async rowEdit(record: Area, changes: Area) {
        if (!this.state.archives) {
            this.props.updateArea([new Area({ ...record, ...changes })]);
        }
    }

    async delete() {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "delete_forever",
            styleColor: "red",
            title: "Archive Duty Areas",
            text: `Are you sure you want to archive the listed duty areas?<br/><ul class="indented chevron"><li>${this.state.selections
                .map(item => item.display)
                .join("</li><li>")}</li></ul>`,
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.archiveArea(this.state.selections);

                this.setState({ selection: null, selections: [] });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    sort() {
        this.setState({
            sorting: this.props.areas
                .map(area => new Area(area))
                .sort(Utility.getSorter([`number`])),
            sortOpen: true,
        });
    }

    async saveSort(sorted: Area[]) {
        try {
            this.setState({ sortLoad: true });

            for (const [newIndex, item] of sorted.entries())
                item.number = newIndex;

            await this.props.updateArea(sorted);

            this.setState({ sortOpen: false });
        } finally {
            this.setState({ sortLoad: false });
        }
    }

    import() {
        this.setState({
            importOpen: true,
            importStep: 0,
            importType: undefined,
        });
    }

    importSetType(type: string) {
        this.setState({ importType: type, importStep: 0 });
    }

    importSetStep(step: number) {
        this.setState({ importStep: step });
    }

    importNextStep() {
        this.setState({ importStep: this.state.importStep + 1 });
    }

    importLastStep() {
        this.setState({ importStep: this.state.importStep - 1 });
    }

    importProceedChanged(valid: boolean) {
        this.setState({ importValid: valid });
    }

    importCancel() {
        this.setState({ importOpen: false });
    }

    render() {
        return (
            <section>
                <MainToolbarContents>
                    <Toggle
                        onLabel="Archives"
                        offLabel="Archives"
                        value={this.state.archives}
                        onChange={value =>
                            this.setState({ archives: value ?? false })
                        }
                    />
                    <ToolbarButton
                        icon="add"
                        text="Add"
                        tooltip="Add a new duty area"
                        onClick={this.add.bind(this)}
                    />
                    <ToolbarButton
                        icon="edit"
                        text="Edit"
                        tooltip={`Edit the selected duty area${
                            this.state.selections.length > 1 ? "s" : ""
                        }`}
                        disabled={this.state.selections.length === 0}
                        onClick={this.edit.bind(this)}
                    />
                    <ToolbarButton
                        icon="archive"
                        text="Archive"
                        tooltip={`Archive the selected duty area${
                            this.state.selections.length > 1 ? "s" : ""
                        }`}
                        disabled={this.state.selections.length === 0}
                        onClick={this.delete.bind(this)}
                    />
                    <ToolbarButton
                        icon="filter_list"
                        text="Sort"
                        tooltip="Sort all duty areas"
                        onClick={this.sort.bind(this)}
                    />
                    <ToolbarButton
                        icon="system_update_alt"
                        text="Import"
                        tooltip="Import rooms"
                        onClick={this.import.bind(this)}
                    />
                </MainToolbarContents>

                {(this.state.archives
                    ? this.props.archives?.length
                    : this.props.areas?.length) > 0 ? (
                    <Grid<Area>
                        value={
                            this.state.archives
                                ? this.props.archives
                                : this.props.areas
                        }
                        window={true}
                        collapsed={true}
                        fixedHeader={true}
                        keyField="id"
                        defaultSortField="number"
                        selectionChanged={(selection, selections) =>
                            this.setState({ selection, selections })
                        }
                        cellDoubleClicked={this.edit.bind(this)}
                        valueEdited={this.rowEdit.bind(this)}>
                        <GridColumn
                            header="No"
                            field="number"
                            selectable={true}
                            alignment={Alignment.Left}
                            width="40px"
                        />
                        <GridColumn
                            type={GridColumnType.Dropdown}
                            header="Campus"
                            field="site"
                            selectable={true}
                            filterable={true}
                            editable={true}
                            options={this.props.campuses.map(c => ({
                                text: c.name,
                                value: c.id,
                            }))}
                        />
                        <GridColumn
                            header="Code"
                            field="code"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                        <GridColumn
                            header="Name"
                            field="name"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                        <GridColumn
                            header="Notes"
                            field="notes"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                    </Grid>
                ) : (
                    <Text clearanceSide={Side.All}>
                        {this.state.archives
                            ? "No archived duty areas found."
                            : "No active duty areas found."}
                    </Text>
                )}

                <Dialog
                    showDialog={this.state.editOpen}
                    showLoading={this.state.editLoad}
                    dialogIcon="edit"
                    dialogTitle={this.state.editTitle}
                    canResize={false}
                    width={600}>
                    <DialogContent>
                        <Panel>
                            <Panel>
                                <InputForm
                                    binding={this.state.editing}
                                    display={RenderOrientations.Vertical}
                                    fieldDisplay={RenderOrientations.Vertical}
                                    onChange={value =>
                                        this.setState({ editing: value })
                                    }>
                                    <InputFormField
                                        label="Code"
                                        field="code"
                                        mandatory={true}
                                    />
                                    <InputFormField
                                        label="Name"
                                        field="name"
                                        mandatory={true}
                                    />
                                </InputForm>
                                <InputForm
                                    binding={this.state.editing}
                                    display={RenderOrientations.Horizontal}
                                    fieldDisplay={RenderOrientations.Vertical}
                                    onChange={value =>
                                        this.setState({ editing: value })
                                    }>
                                    <InputFormField
                                        type={InputFieldTypes.Select}
                                        label="Campus"
                                        field="site"
                                        options={this.props.campuses.map(c => ({
                                            text: c.name,
                                            value: c.id,
                                        }))}
                                    />
                                </InputForm>
                                <InputForm
                                    binding={this.state.editing}
                                    display={RenderOrientations.Horizontal}
                                    fieldDisplay={RenderOrientations.Vertical}
                                    onChange={value =>
                                        this.setState({ editing: value })
                                    }>
                                    <InputFormField
                                        type={InputFieldTypes.TextMulti}
                                        label="Notes"
                                        field="notes"
                                    />
                                </InputForm>
                            </Panel>
                        </Panel>

                        <InputFormMandatory />
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Cancel"
                            onClick={this.cancelEdit.bind(this)}
                        />
                        <DialogOK
                            text="Save"
                            onClick={this.saveEdit.bind(this)}
                            disabled={!this.canSave()}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.importOpen}
                    showLoading={this.state.importLoad}
                    dialogIcon="system_update_alt"
                    dialogTitle="Import"
                    canResize={false}
                    width={this.state.importWidth}>
                    <DialogContent>
                        <ConnectedImportWizard
                            importType={this.state.importType}
                            importStep={this.state.importStep}
                            setType={this.importSetType.bind(this)}
                            setStep={this.importSetStep.bind(this)}
                            validChanged={this.importProceedChanged.bind(this)}
                            setLoading={value =>
                                this.setState({ importLoad: value })
                            }
                        />
                    </DialogContent>
                    <DialogFooter>
                        {this.state.importStep !== 1 && (
                            <DialogCancel
                                text={
                                    this.state.importStep < 1
                                        ? "Cancel"
                                        : "Close"
                                }
                                onClick={this.importCancel.bind(this)}
                            />
                        )}
                        {this.state.importStep < 1 && (
                            <DialogOK
                                text="Next"
                                disabled={!this.state.importValid}
                                onClick={this.importNextStep.bind(this)}
                            />
                        )}
                    </DialogFooter>
                </Dialog>

                <SortDialog<Area>
                    keyField="id"
                    value={this.state.sorting}
                    showDialog={this.state.sortOpen}
                    showLoading={this.state.sortLoad}
                    onSave={this.saveSort.bind(this)}
                    onCancel={() => this.setState({ sortOpen: false })}>
                    <GridColumn
                        header="No"
                        field="number"
                        alignment={Alignment.Left}
                        width="40px"
                    />
                    <GridColumn
                        type={GridColumnType.Dropdown}
                        header="Campus"
                        field="site"
                        options={this.props.campuses.map(c => ({
                            text: c.name,
                            value: c.id,
                        }))}
                    />
                    <GridColumn
                        header="Code"
                        field="code"
                    />
                    <GridColumn
                        header="Name"
                        field="name"
                    />
                    <GridColumn
                        header="Notes"
                        field="notes"
                    />
                </SortDialog>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { area, campus, yearLevel } = state.db;
    return {
        campuses: campus.campuses,
        archives: area.archives,
        areas: area.areas,
        yearLevels: yearLevel.yearLevels,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseAreaDetails: () => unwrappedDispatch(initialiseAreaDetails()),
        initialiseCampusDetails: () =>
            unwrappedDispatch(initialiseCampusDetails()),
        addArea: (details: Area[]) => unwrappedDispatch(addArea(details)),
        updateArea: (details: Area[]) => unwrappedDispatch(updateArea(details)),
        archiveArea: (details: Area[]) =>
            unwrappedDispatch(archiveArea(details)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteAreas);

interface ImportWizardProps {
    areas: Area[];
    importType?: string;
    importStep: number;

    addArea: (details: Area[]) => Promise<void>;
    updateArea: (details: Area[]) => Promise<void>;
    setType?: (type: string) => void;
    setStep?: (step: number) => void;
    setLoading?: (loading: boolean) => void;
    validChanged?: (valid: boolean) => void;
}

interface ImportWizardState {
    importData?: DataSet;
    importFile?: File;
    importLogs?: ImportLog;
}

class ImportWizard extends React.Component<
    ImportWizardProps,
    ImportWizardState
> {
    state: ImportWizardState = {};

    private importTypes = [
        { value: "csv", text: "XLSX/CSV", order: 1 },
        { value: "tdf", text: "TD File", order: 2 },
    ];

    private importConfig: SpreadsheetConfiguration = {
        Name: "Duty Areas",
        Tables: [
            {
                Name: "Duty Areas",
                Columns: [
                    { PK: true, Field: "code", Name: "Code" },
                    { Field: "name", Name: "Name" },
                    { Field: "notes", Name: "Notes" },
                    { Field: "site", Name: "Campus" },
                    { Field: "number", Name: "Number" },
                ],
            },
        ],
    };

    componentDidMount(): void {
        this.setState({
            importData: undefined,
        });
    }

    componentDidUpdate(
        prevProps: Readonly<ImportWizardProps>,
        prevState: Readonly<ImportWizardState>,
        snapshot?: any,
    ): void {
        if (prevProps.importType !== this.props.importType) {
            this.setState({
                importData: undefined,
                importFile: undefined,
            });

            this.props.validChanged?.(false);
        }

        if (prevProps.importStep !== this.props.importStep) {
            this.props.validChanged?.(this.importCanProceed());

            this.importProceed();
        } else if (prevState !== this.state) {
            this.props.validChanged?.(this.importCanProceed());
        }
    }

    importCanProceed(): boolean {
        switch (this.props.importStep) {
            case 0:
                switch (this.props.importType) {
                    case "csv":
                        return this.state.importData !== undefined;
                    case "tdf":
                        return this.state.importFile !== undefined;
                    default:
                        return false;
                }
            default:
                return false;
        }
    }

    importProceed() {
        switch (this.props.importStep) {
            case 1:
                switch (this.props.importType) {
                    case "csv":
                        this.doCsvImport();
                        break;
                    case "tdf":
                        this.doTDImport();
                        break;
                }
                break;
        }
    }

    async doCsvImport() {
        try {
            this.props.setStep?.(1);

            const adds = [];
            const upd = [];
            const logs: ImportLog = {
                upsertCounts: [],
                skippedItems: [],
            };

            for (const config of this.importConfig.Tables) {
                const table = this.state.importData[config.Name];
                if (!table) continue;

                this.processCsvImport(config, table, adds, upd, logs);
            }

            const waits: Promise<void>[] = [];
            if (adds.length > 0) waits.push(this.props.addArea(adds));
            if (upd.length > 0) waits.push(this.props.updateArea(upd));
            await Promise.all(waits);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(2);
        } catch {
            this.props.setStep?.(0);
        }
    }

    processCsvImport(
        type: SpreadsheetTableDefinition,
        data: DataTable,
        adds: Area[],
        upd: Area[],
        logs: ImportLog,
    ): void {
        for (const row of data) {
            const pk = type.Columns.find(c => c.PK)?.Field;
            if (!pk) continue;

            const pkVal = row[pk]?.toString().trim();
            if (!pkVal) continue;

            let area = this.props.areas?.find(
                u => u[pk]?.toLowerCase() === pkVal.toLowerCase(),
            );

            if (!area) {
                area = new Area({
                    [pk]: pkVal,
                });

                adds.push(area);
            } else {
                area = new Area({ ...area });
                if (!upd.includes(area)) upd.push(area);
            }

            for (const column of type.Columns) {
                if (column.PK) continue;

                const value = row[column.Field]?.toString().trim();
                if (!value) continue;

                switch (column.Field) {
                    default:
                        Reflect.set(area, column.Field as keyof Area, value);
                        break;
                }
            }
        }

        logs.upsertCounts.push({
            name: type.Name,
            insertCount: adds.length,
            updateCount: upd.length,
            archiveCount: 0,
        });
    }

    async doTDImport() {
        try {
            this.props.setStep?.(1);

            const logs = await importAreasFromTD(this.state.importFile.id);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(2);
        } catch {
            this.props.setStep?.(0);
        }
    }

    render() {
        return (
            <>
                <SelectList
                    tabs={true}
                    value={this.props.importType}
                    options={this.importTypes}
                    onChange={value =>
                        this.props.importStep === 0 &&
                        this.props.setType?.(value)
                    }
                />

                <Wizard
                    flow={true}
                    frameless={true}
                    transparent={true}
                    clearanceSide={Side.Top}
                    step={this.props.importStep}>
                    <WizardStep step={0}>
                        {(() => {
                            switch (this.props.importType) {
                                case "csv":
                                    return (
                                        <Import
                                            config={this.importConfig}
                                            template={template}
                                            templateName={template.stripReactName()}
                                            onReady={(_, data) =>
                                                this.setState({
                                                    importData: data,
                                                })
                                            }
                                        />
                                    );
                                case "tdf":
                                    return (
                                        <FileExplorer
                                            type={FileExplorerTypes.Timetables}
                                            value={this.state.importFile}
                                            onChange={item =>
                                                this.setState({
                                                    importFile: item,
                                                })
                                            }
                                        />
                                    );
                                default:
                                    return null;
                            }
                        })()}
                    </WizardStep>
                    <WizardStep
                        step={1}
                        framed={true}
                        hiddenWhenOffscreen={true}>
                        <div className="buffer text-center">
                            <h2>We're currently importing your data</h2>
                        </div>
                        <div className="process-indicator">
                            <div className="cube cube1"></div>
                            <div className="cube cube2"></div>
                            <div className="cube cube3"></div>
                            <div className="cube cube4"></div>
                            <div className="cube cube5"></div>
                            <div className="cube cube6"></div>
                            <div className="cube cube7"></div>
                            <div className="cube cube8"></div>
                            <div className="cube cube9"></div>
                            <div className="cube cube10"></div>
                            <div className="cube cube11"></div>
                            <div className="document1"></div>
                            <div className="document2"></div>
                        </div>
                    </WizardStep>
                    <WizardStep
                        step={2}
                        hiddenWhenOffscreen={true}>
                        <Panel>
                            <Panel
                                title="Addition/Update Counts"
                                bufferSide={Side.All}>
                                {this.state?.importLogs?.upsertCounts?.length >
                                0 ? (
                                    <Grid<UpsertCount>
                                        value={
                                            this.state.importLogs
                                                ?.upsertCounts ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Added"
                                            field="insertCount"
                                        />
                                        <GridColumn
                                            header="Updated"
                                            field="updateCount"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>
                                        No records were added or updated.
                                    </Info>
                                )}
                            </Panel>

                            <Panel
                                title="Skipped Items"
                                bufferSide={Side.All}
                                clearanceSide={Side.Top}>
                                {this.state?.importLogs?.skippedItems?.length >
                                0 ? (
                                    <Grid<SkippedRecordCount>
                                        value={
                                            this.state.importLogs
                                                ?.skippedItems ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Number"
                                            field="count"
                                        />
                                        <GridColumn
                                            header="Reason"
                                            field="reason"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>No records were skipped.</Info>
                                )}
                            </Panel>
                        </Panel>
                    </WizardStep>
                </Wizard>
            </>
        );
    }
}

const mapStateToPropsWizard = (state: IAppState) => {
    const { area } = state.db;
    return {
        areas: area.areas,
    };
};

const mapDispatchToPropsWizard = (_dispatch: AppDispatch) => {
    return {
        addArea: (details: Area[]) => unwrappedDispatch(addArea(details)),
        updateArea: (details: Area[]) => unwrappedDispatch(updateArea(details)),
    };
};

const ConnectedImportWizard = connect(
    mapStateToPropsWizard,
    mapDispatchToPropsWizard,
)(ImportWizard);
